/**
 * Kitchen tablet enrolment (HMH-2417).
 *
 * Done once per tablet: an admin adds the tablet under Admin > Kitchen Staff and
 * gets a one-time code; typing it here stores the tablet's device secret (an
 * HttpOnly cookie the page never sees) and opens the kitchen display.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiConfig } from "../config/msalConfig";

export function KitchenEnrollPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const response = await fetch(`${apiConfig.baseUrl}/auth/kitchen/enroll`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        setError(body.detail || "Enrolment failed");
        return;
      }
      // A clean start: the kitchen display signs itself in with the new device cookie
      sessionStorage.removeItem("hmh_refresh_token");
      sessionStorage.removeItem("hmh_access_token");
      window.location.assign("/admin/orders");
    } catch {
      setError("Could not reach the server. Check the network and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Set up this kitchen tablet</h1>
          <p className="text-sm text-gray-600">Hood Memorial Hospital</p>
        </div>
        <div className="bg-white rounded-lg shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">{error}</div>
            )}
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                Enrolment code from an administrator
              </label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                autoFocus
                maxLength={16}
                disabled={busy}
                className="w-full px-4 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-2xl tracking-widest text-center"
                placeholder="XXXXX-XXXXX"
              />
            </div>
            <button
              type="submit"
              disabled={busy || !code.trim()}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {busy ? "Enrolling..." : "Enrol tablet"}
            </button>
          </form>
          <div className="mt-6 text-center">
            <button onClick={() => navigate("/kitchen")} className="text-sm text-blue-600 hover:text-blue-800 font-medium">
              Back to Kitchen Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
